import { useEffect, useState } from 'react';
import { getDaily } from '@/api/dashboard-live';
import type { DailyPoint } from '@/api/dashboard-live';
import { cn } from '@/lib/utils';
import { usePilotage } from '../context';
import { Panel } from '../ui';
import { DailyChart, Legend } from '../charts';
import type { SeriesDef } from '../charts';
import { compact, dec, ms, num } from '../format';

const RANGES = [
  { days: 30, label: '30 j' },
  { days: 90, label: '3 mois' },
  { days: 365, label: '1 an' },
  { days: 730, label: '2 ans' },
];

/** Écran 7 — Historique long (valeurs de fin de journée) */
export function HistoryScreen() {
  const { palette: p, day, snapshot } = usePilotage();
  const [days, setDays] = useState(90);
  const [data, setData] = useState<Record<string, DailyPoint[]>>({});

  const charts: { title: string; subtitle?: string; defs: SeriesDef[]; format?: (n: number) => string; domainMax?: number; bars?: boolean }[] = [
    {
      title: 'Utilisateurs actifs par jour',
      subtitle: 'V1 : web + mobile à partir du 01/10/2026, connexions explicites seules avant (≈ -25 %)',
      defs: [
        { metric: 'v2.dau', label: 'V2', color: p.v2 },
        { metric: 'v1.dau', label: 'V1', color: p.v1 },
      ],
    },
    {
      title: 'Part d\'usage V2',
      defs: [{ metric: 'migration.usage_v2_pct', label: 'Part d\'usage V2', color: p.v2 }],
      format: (n) => `${dec(n, 0)} %`,
      domainMax: 100,
    },
    {
      title: 'Nouveaux utilisateurs V2',
      subtitle: 'personnes créées dans la journée',
      defs: [
        { metric: 'v2.new.student', label: 'Élèves', color: p.v2 },
        { metric: 'v2.new.parent', label: 'Parents', color: p.v1 },
      ],
      bars: true,
    },
    {
      title: 'Élèves (stock)',
      subtitle: 'cron ef:metrics:get',
      defs: [
        { metric: 'v2.stock.student', label: 'V2', color: p.v2 },
        { metric: 'v1.stock.student', label: 'V1', color: p.v1 },
      ],
      format: compact,
    },
    {
      title: 'Activité V2',
      defs: [
        { metric: 'v2.activity.attendanceCalls', label: 'Appels', color: p.v2 },
        { metric: 'v2.activity.grades', label: 'Notes', color: p.v1 },
        { metric: 'v2.activity.messages', label: 'Messages', color: p.third },
      ],
    },
    {
      title: 'Requêtes par jour',
      subtitle: 'logs d\'ingress (7 jours de rétention Loki, puis historisé ici)',
      defs: [
        { metric: 'api.v2-api.req_day', label: 'API V2', color: p.v2 },
        { metric: 'api.v1.req_day', label: 'V1', color: p.v1 },
      ],
      format: compact,
    },
    {
      title: 'Temps de réponse p95 du jour',
      defs: [
        { metric: 'api.v2-api.p95_ms_day', label: 'API V2', color: p.v2 },
        { metric: 'api.v1.p95_ms_day', label: 'V1', color: p.v1 },
      ],
      format: ms,
    },
    {
      title: 'Taux d\'erreur 5xx du jour',
      defs: [
        { metric: 'api.v2-api.err_pct_day', label: 'API V2', color: p.v2 },
        { metric: 'api.v1.err_pct_day', label: 'V1', color: p.v1 },
      ],
      format: (n) => `${dec(n, 2)} %`,
    },
    {
      title: 'E-mails V2',
      defs: [
        { metric: 'v2.activity.mails', label: 'Envoyés', color: p.v2 },
        { metric: 'v2.activity.mailsFailed', label: 'En échec', color: p.v1 },
      ],
      format: num,
    },
  ];

  useEffect(() => {
    let cancelled = false;
    const metrics = [...new Set(charts.flatMap((c) => c.defs.map((d) => d.metric)))];
    getDaily(metrics, { days, to: day })
      .then((r) => !cancelled && setData(r))
      .catch(() => !cancelled && setData({}));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, day, snapshot.day]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex items-center gap-1.5">
        {RANGES.map((r) => (
          <button
            key={r.days}
            type="button"
            onClick={() => setDays(r.days)}
            className={cn(
              'rounded-md px-3 py-1 text-sm',
              days === r.days ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
            )}
          >
            {r.label}
          </button>
        ))}
        <span className="ml-3 text-xs text-slate-400">Une valeur par jour : photo de fin de journée, corrigée par le recalcul exact de la journée complète.</span>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 xl:grid-rows-3">
        {charts.map((c) => (
          <Panel key={c.title} title={c.title} subtitle={c.subtitle} right={<Legend defs={c.defs} />}>
            <DailyChart defs={c.defs} format={c.format} domainMax={c.domainMax} bars={c.bars} source={data} height="100%" />
          </Panel>
        ))}
      </div>
    </div>
  );
}
